import { useState, useRef, useCallback, useEffect } from 'react'

const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
    : null

const SEND_TRIGGERS = [
  'elettra invia',
  'elettra, invia',
  'invia messaggio',
  'vai elettra',
  'vai, elettra',
  'elettra vai',
  'elettra, vai',
]

/**
 * Converte parole di punteggiatura dettate nei simboli corrispondenti.
 * NB: l'ordine è importante — "punto e virgola" prima di "punto" e "virgola"
 */
function processVoicePunctuation(text: string): string {
  return text
    .replace(/\bpunto e virgola\b/gi, ';')
    .replace(/\bpunto interrogativo\b/gi, '?')
    .replace(/\bpunto esclamativo\b/gi, '!')
    .replace(/\bdue punti\b/gi, ':')
    .replace(/\ba capo\b/gi, '\n')
    .replace(/\bvirgola\b/gi, ',')
    .replace(/\bpunto\b/gi, '.')
    .replace(/\s+([.,;:?!])/g, '$1')
}

function detectSendTrigger(text: string): { cleaned: string } | null {
  const lower = text.toLowerCase().trimEnd()
  for (const trigger of SEND_TRIGGERS) {
    if (lower.endsWith(trigger)) {
      const cleaned = text.substring(0, lower.lastIndexOf(trigger)).trimEnd()
      return { cleaned }
    }
  }
  return null
}

/**
 * Sceglie la migliore alternativa per confidence tra i risultati.
 */
function getBestTranscript(result: any): string {
  if (result.length <= 1) return result[0].transcript
  let best = result[0]
  for (let j = 1; j < result.length; j++) {
    if (result[j].confidence > best.confidence) {
      best = result[j]
    }
  }
  return best.transcript
}

interface UseSpeechRecognitionOptions {
  onTranscript: (text: string) => void
  onSendCommand: () => void
}

interface UseSpeechRecognitionReturn {
  isListening: boolean
  start: () => void
  stop: () => void
  syncTranscript: (text: string) => void
}

export function useSpeechRecognition({
  onTranscript,
  onSendCommand,
}: UseSpeechRecognitionOptions): UseSpeechRecognitionReturn {
  const [isListening, setIsListening] = useState(false)
  const recognitionRef = useRef<any>(null)
  const finalTranscriptRef = useRef('')
  const sendTriggeredRef = useRef(false)
  const onTranscriptRef = useRef(onTranscript)
  const onSendCommandRef = useRef(onSendCommand)
  // true = utente ha esplicitamente fermato con stop(), false = disponibile per auto-restart
  const userStoppedRef = useRef(true)

  onTranscriptRef.current = onTranscript
  onSendCommandRef.current = onSendCommand

  const stop = useCallback(() => {
    userStoppedRef.current = true
    if (recognitionRef.current) {
      recognitionRef.current.abort()
    }
    recognitionRef.current = null
    setIsListening(false)
  }, [])

  const startInternal = useCallback((preserveTranscript: boolean) => {
    if (!SpeechRecognitionAPI) return

    // Se non preserva, resetta tutto
    if (!preserveTranscript) {
      finalTranscriptRef.current = ''
    }
    sendTriggeredRef.current = false
    userStoppedRef.current = false

    // Kill istanza precedente se esiste
    if (recognitionRef.current) {
      try { recognitionRef.current.abort() } catch {}
      recognitionRef.current = null
    }

    const recognition = new SpeechRecognitionAPI()
    recognition.lang = 'it-IT'
    recognition.continuous = true
    recognition.interimResults = true
    recognition.maxAlternatives = 3

    recognition.onstart = () => setIsListening(true)

    recognition.onresult = (event: any) => {
      if (sendTriggeredRef.current) return

      let interim = ''
      let finalSegment = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalSegment += getBestTranscript(result)
        } else {
          // Per interim usa prima alternativa (le altre potrebbero non avere confidence)
          interim += result[0].transcript
        }
      }

      if (finalSegment) {
        finalTranscriptRef.current += finalSegment
      }

      const fullText = finalTranscriptRef.current + interim

      const trigger = detectSendTrigger(fullText)
      if (trigger) {
        sendTriggeredRef.current = true
        userStoppedRef.current = true // previeni auto-restart dopo send
        onTranscriptRef.current(processVoicePunctuation(trigger.cleaned))
        if (recognitionRef.current) {
          recognitionRef.current.stop()
        }
        setTimeout(() => onSendCommandRef.current(), 100)
        return
      }

      onTranscriptRef.current(processVoicePunctuation(fullText))
    }

    recognition.onerror = (event: any) => {
      if (event.error === 'aborted') return // expected on stop()
      if (event.error !== 'no-speech') {
        console.warn('[ELETTRA STT] Error:', event.error)
      }
      // "no-speech" non è fatale — onend gestirà il restart
    }

    recognition.onend = () => {
      recognitionRef.current = null
      // Auto-restart se l'utente non ha fermato esplicitamente
      if (!userStoppedRef.current && !sendTriggeredRef.current) {
        // Chrome interrompe periodicamente anche con continuous=true
        // Riavvia preservando il transcript accumulato
        setTimeout(() => {
          if (!userStoppedRef.current) {
            startInternal(true)
          }
        }, 200)
      } else {
        setIsListening(false)
      }
    }

    recognitionRef.current = recognition
    try {
      recognition.start()
    } catch (e) {
      console.warn('[ELETTRA STT] Start failed:', e)
      recognitionRef.current = null
      setIsListening(false)
    }
  }, [])

  const start = useCallback(() => {
    startInternal(false)
  }, [startInternal])

  // Sincronizza il transcript interno con il testo della textarea.
  // Chiamare quando l'utente modifica manualmente il testo.
  const syncTranscript = useCallback((text: string) => {
    finalTranscriptRef.current = text
  }, [])

  useEffect(() => {
    return () => {
      userStoppedRef.current = true
      if (recognitionRef.current) {
        recognitionRef.current.abort()
        recognitionRef.current = null
      }
    }
  }, [])

  return { isListening, start, stop, syncTranscript }
}
